import { useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Stack, Chip,
  Typography, IconButton, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Paper, Button, Avatar, Divider, useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Close as CloseIcon, Map as MapIcon,
  CheckCircle as OkIcon, ErrorOutline as OweIcon,
  TrendingUp as OverIcon, TrendingDown as OweTrendIcon,
  Today as TodayIcon,
} from '@mui/icons-material';

const TNUM = { fontVariantNumeric: 'tabular-nums' };
const fmt = (n) => Number(n || 0).toLocaleString(undefined,
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Which days the projection charges on.
//
// Not a school-day source of truth — the actual "is today a school day?"
// decision lives on the daily_sessions table (opened per day). This only
// says which days the trail below EXPECTS to be charged, and it reads
// the school's configured collection week (Settings → Academics →
// School Week) so a school that collects on Saturdays sees Saturdays in
// its forecast. Falls back to Monday–Friday when the caller has no
// answer to hand, which is what this assumed before it was configurable.
const MON_TO_FRI = [false, true, true, true, true, true, false];

const makeIsSchoolDay = (workingDays) => {
  const days = Array.isArray(workingDays) && workingDays.length === 7
    ? workingDays : MON_TO_FRI;
  return (d) => days[d.getDay()] !== false;
};

const shortDate = (d) => d.toLocaleDateString(undefined,
  { weekday: 'short', month: 'short', day: 'numeric' });

// Walks N future school days forward using the "what if approved"
// balance. Each day charges daily_amount; credit is consumed first,
// otherwise arrears grow.
function buildProjection({ daily, startingCredit, startingArrears, days, workingDays }) {
  const out = [];
  const isSchoolDay = makeIsSchoolDay(workingDays);
  let credit  = Number(startingCredit)  || 0;
  let arrears = Number(startingArrears) || 0;
  const d = new Date();
  d.setDate(d.getDate() + 1);   // start from tomorrow
  let looked = 0, safety = 0;
  while (looked < days && safety < 200) {
    safety += 1;
    if (isSchoolDay(d)) {
      let paidFromCredit = 0;
      let addedArrears   = 0;
      if (credit >= daily) {
        paidFromCredit = daily;
        credit -= daily;
      } else if (credit > 0) {
        paidFromCredit = credit;
        addedArrears   = daily - credit;
        credit = 0;
        arrears += addedArrears;
      } else {
        addedArrears = daily;
        arrears += daily;
      }
      out.push({
        date: new Date(d),
        paidFromCredit,
        addedArrears,
        credit,
        arrears,
      });
      looked += 1;
    }
    d.setDate(d.getDate() + 1);
  }
  return out;
}

// MUI palette lookup that survives when the requested tone doesn't have
// a .main slot (e.g. 'grey' is a scale, not a variant). Falls back to
// primary for unknown tones so we never dereference `undefined`.
function paletteKey(tone) {
  switch (tone) {
    case 'money': return 'primary';
    case 'good':  return 'success';
    case 'warn':  return 'warning';
    case 'bad':   return 'error';
    default:      return null;    // signals "neutral grey" — handled below
  }
}

function StatCard({ label, value, tone = 'default', hint }) {
  const key = paletteKey(tone);
  return (
    <Box sx={{
      p: 1, borderRadius: 1.5,
      bgcolor: (theme) => key
        ? alpha(theme.palette[key].main, 0.08)
        : theme.palette.grey[50],
      border: '1px solid',
      borderColor: (theme) => key
        ? alpha(theme.palette[key].main, 0.30)
        : theme.palette.divider,
      height: '100%',
      minWidth: 0,
    }}>
      <Typography sx={{
        color: key ? `${key}.dark` : 'text.secondary',
        fontWeight: 700, letterSpacing: 0.4,
        textTransform: 'uppercase', fontSize: 9.5, lineHeight: 1.2,
      }}>
        {label}
      </Typography>
      <Typography sx={{
        color: key ? `${key}.dark` : 'text.primary',
        fontWeight: 800, fontSize: 15, lineHeight: 1.15,
        mt: 0.15, ...TNUM,
      }}>
        {value}
      </Typography>
      {hint && (
        <Typography sx={{ color: 'text.secondary', display: 'block',
          mt: 0.15, fontSize: 10, lineHeight: 1.2 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

function OutcomeChip({ paidFromCredit, daily, addedArrears }) {
  if (paidFromCredit >= daily && daily > 0) {
    return <Chip size="small" color="success" variant="filled"
      icon={<OkIcon sx={{ fontSize: 14, color: '#fff' }} />}
      label="Covered by credit"
      sx={{ fontWeight: 700, height: 22, color: '#fff' }} />;
  }
  if (paidFromCredit > 0) {
    return <Chip size="small" color="warning" variant="outlined"
      label={`Partial from credit`}
      sx={{ fontWeight: 700, height: 22 }} />;
  }
  return <Chip size="small" color="error" variant="outlined"
    icon={<OweIcon fontSize="small" />} label="Owed"
    sx={{ fontWeight: 700, height: 22 }} />;
}

// `workingDays` is the school's configured collection week as seven
// booleans, Sunday first — the same array the day sheet already
// receives from the server. Optional: without it the forecast falls
// back to Monday–Friday.
export default function DistributionMapDialog({ open, row, onClose, workingDays }) {
  // Hooks must run before any early return in this component, so the
  // full-screen check lives here rather than next to the <Dialog>.
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const derived = useMemo(() => {
    if (!row) return null;
    const daily         = Number(row.daily_amount)   || 0;
    const amountPaid    = Number(row.amount_paid)    || 0;
    const savedCredit   = Number(row.credit)         || 0;
    const savedArrears  = Number(row.arrears)        || 0;
    const creditUsedTdy = Number(row.credit_used)    || 0;
    const arrearsToday  = Number(row.arrears_added)  || 0;
    const status        = String(row.status || '').toLowerCase();

    // Overflow settles only at admin approval, but the map is a "what if
    // approved" preview — include it as effective credit so the picture
    // matches the collector's intent.
    const chargeToday    = status === 'absent' ? 0 : daily;
    const chargeAfterCr  = Math.max(0, chargeToday - creditUsedTdy);
    const pendingOverflow = Math.max(0, amountPaid - chargeAfterCr);

    // For the projection we also assume that pending overflow first pays
    // down any lingering saved arrears (that's how the backend applies it
    // on approval), then the remainder becomes credit.
    let projArrears = savedArrears;
    let projCredit  = savedCredit;
    let overflow    = pendingOverflow;
    if (overflow > 0 && projArrears > 0) {
      const clear = Math.min(overflow, projArrears);
      projArrears -= clear;
      overflow    -= clear;
    }
    projCredit += overflow;

    // Coverage in days that the credit buys you at today's rate.
    const daysAhead = daily > 0 ? Math.floor(projCredit / daily) : 0;
    const leftover  = daily > 0 ? projCredit - daysAhead * daily : 0;

    return {
      daily, amountPaid, status,
      savedCredit, savedArrears, arrearsToday, pendingOverflow,
      projCredit, projArrears, daysAhead, leftover,
    };
  }, [row]);

  const projection = useMemo(() => {
    if (!derived) return [];
    return buildProjection({
      daily:           derived.daily,
      startingCredit:  derived.projCredit,
      startingArrears: derived.projArrears,
      days:            10,
      workingDays,
    });
  }, [derived, workingDays]);

  if (!row || !derived) return null;

  const {
    daily, amountPaid, status,
    savedCredit, savedArrears, arrearsToday, pendingOverflow,
    projCredit, projArrears, daysAhead,
  } = derived;

  // The lead message adapts to what happened today.
  let leadTitle = '';
  let leadTone  = 'default';
  let leadIcon  = null;
  if (status === 'absent') {
    leadTitle = 'Student was absent today — no charge, no arrears added.';
    leadTone  = 'default';
    leadIcon  = <TodayIcon />;
  } else if (pendingOverflow > 0 && daysAhead > 0) {
    leadTitle = `This payment covers today + ${daysAhead} more school day${daysAhead === 1 ? '' : 's'} at GHS ${fmt(daily)}.`;
    leadTone  = 'good';
    leadIcon  = <OverIcon />;
  } else if (pendingOverflow > 0) {
    leadTitle = `Overflow of GHS ${fmt(pendingOverflow)} will become credit on approval.`;
    leadTone  = 'good';
    leadIcon  = <OverIcon />;
  } else if (arrearsToday > 0) {
    leadTitle = `Shortfall of GHS ${fmt(arrearsToday)} was added to arrears today.`;
    leadTone  = 'bad';
    leadIcon  = <OweTrendIcon />;
  } else if (projArrears > 0) {
    leadTitle = `Arrears carrying forward: GHS ${fmt(projArrears)}.`;
    leadTone  = 'warn';
    leadIcon  = <OweTrendIcon />;
  } else {
    leadTitle = 'Today is settled. No credit, no arrears.';
    leadTone  = 'default';
    leadIcon  = <OkIcon />;
  }

  const leadColour = leadTone === 'good' ? 'success'
    : leadTone === 'warn' ? 'warning'
    : leadTone === 'bad'  ? 'error'
    : 'primary';

  return (
    // Full-screen on a phone. At maxWidth="md" the dialog was a small
    // window inside a small screen, leaving the projection table
    // squeezed into a couple of hundred pixels.
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      fullScreen={fullScreen}>
      <DialogTitle sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
        color: '#fff', display: 'flex', alignItems: 'center', gap: 1.2, py: 1.3,
      }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)', width: 32, height: 32,
          border: '2px solid rgba(255,255,255,0.55)' }}>
          <MapIcon sx={{ color: '#fff', fontSize: 18 }} />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ lineHeight: 1.1, fontSize: 15, fontWeight: 900 }}>
            {row.student_name || row.student_id}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 11 }}>
            Distribution map
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: 'background.default', p: 1.5 }}>
        {/* Lead banner — the human takeaway in one line */}
        <Box sx={{
          p: 1.1, borderRadius: 1.5, mb: 1.2,
          bgcolor: (t) => alpha(t.palette[leadColour].main, 0.09),
          border: '1px solid',
          borderColor: (t) => alpha(t.palette[leadColour].main, 0.35),
          display: 'flex', alignItems: 'center', gap: 1,
        }}>
          <Box sx={{ color: `${leadColour}.main`, display: 'flex' }}>
            {leadIcon}
          </Box>
          <Typography sx={{ fontWeight: 700, color: `${leadColour}.dark`, fontSize: 13 }}>
            {leadTitle}
          </Typography>
        </Box>

        {/* Financial state — five compact stat cards laid out with flex
            so we don't need Grid's fractional column values. */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
          gap: 0.8, mb: 1.2,
        }}>
          <StatCard label="Daily rate" tone="money" value={`GHS ${fmt(daily)}`} />
          <StatCard label="Paid today" tone={amountPaid > 0 ? 'good' : 'default'}
            value={`GHS ${fmt(amountPaid)}`}
            hint={status ? status.toUpperCase() : ''} />
          <StatCard label="Pending overflow" tone={pendingOverflow > 0 ? 'good' : 'default'}
            value={`GHS ${fmt(pendingOverflow)}`}
            hint={pendingOverflow > 0 ? 'Becomes credit on approval' : '—'} />
          <StatCard label="Projected credit" tone={projCredit > 0 ? 'good' : 'default'}
            value={`GHS ${fmt(projCredit)}`}
            hint={daysAhead > 0 ? `${daysAhead} day${daysAhead === 1 ? '' : 's'} covered` : '—'} />
          <StatCard label="Arrears" tone={projArrears > 0 ? 'bad' : 'default'}
            value={`GHS ${fmt(projArrears)}`}
            hint={arrearsToday > 0 ? `+ GHS ${fmt(arrearsToday)} today` : (projArrears > 0 ? 'Carrying forward' : '—')} />
        </Box>

        {/* Projection table */}
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small" sx={{
            '& .MuiTableCell-head': {
              bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
              fontSize: 10, fontWeight: 800,
              textTransform: 'uppercase', letterSpacing: 0.4,
              color: 'primary.dark', py: 0.6,
            },
            '& .MuiTableCell-body': { py: 0.5, fontSize: 11.5 },
          }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 130 }}>Date</TableCell>
                <TableCell align="right" sx={{ width: 90 }}>Charge</TableCell>
                <TableCell align="right" sx={{ width: 130 }}>Paid from credit</TableCell>
                <TableCell align="right" sx={{ width: 110 }}>New arrears</TableCell>
                <TableCell align="right" sx={{ width: 110 }}>Credit left</TableCell>
                <TableCell align="right" sx={{ width: 120 }}>Total arrears</TableCell>
                <TableCell sx={{ width: 170 }}>Outcome</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Today row — visual anchor before the projection */}
              <TableRow sx={{
                bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                borderBottom: '2px solid',
                borderBottomColor: 'primary.light',
              }}>
                <TableCell>
                  <Stack direction="row" spacing={0.6} alignItems="center">
                    <TodayIcon fontSize="small" color="primary" />
                    <Typography sx={{ fontWeight: 800, fontSize: 12 }}>Today</Typography>
                  </Stack>
                </TableCell>
                <TableCell align="right" sx={{ ...TNUM, fontSize: 12, fontWeight: 700 }}>
                  {status === 'absent' ? '—' : fmt(daily)}
                </TableCell>
                <TableCell align="right" sx={{ ...TNUM, fontSize: 12,
                  color: 'text.secondary' }}>
                  {row.credit_used > 0 ? fmt(row.credit_used) : '—'}
                </TableCell>
                <TableCell align="right" sx={{ ...TNUM, fontSize: 12,
                  color: arrearsToday > 0 ? 'error.main' : 'text.disabled',
                  fontWeight: arrearsToday > 0 ? 700 : 500 }}>
                  {arrearsToday > 0 ? fmt(arrearsToday) : '—'}
                </TableCell>
                <TableCell align="right" sx={{ ...TNUM, fontSize: 12,
                  color: projCredit > 0 ? 'success.dark' : 'text.disabled',
                  fontWeight: projCredit > 0 ? 700 : 500 }}>
                  {fmt(projCredit)}
                </TableCell>
                <TableCell align="right" sx={{ ...TNUM, fontSize: 12,
                  color: projArrears > 0 ? 'error.main' : 'text.disabled',
                  fontWeight: projArrears > 0 ? 700 : 500 }}>
                  {fmt(projArrears)}
                </TableCell>
                <TableCell>
                  {status === 'absent'
                    ? <Chip size="small" variant="outlined" label="Absent" sx={{ fontWeight: 700, height: 22 }} />
                    : status === 'full'
                      ? <Chip size="small" color="success" variant="filled" label="Full paid" icon={<OkIcon sx={{ fontSize: 14, color: '#fff' }} />} sx={{ fontWeight: 700, height: 22, color: '#fff' }} />
                      : status === 'partial'
                        ? <Chip size="small" color="warning" variant="outlined" label="Partial" sx={{ fontWeight: 700, height: 22 }} />
                        : status === 'none'
                          ? <Chip size="small" color="error" variant="outlined" label="Didn't pay" sx={{ fontWeight: 700, height: 22 }} />
                          : <Chip size="small" variant="outlined" label="Not marked" sx={{ fontWeight: 700, height: 22 }} />}
                </TableCell>
              </TableRow>

              {projection.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Enter a daily rate to see the projection.
                  </TableCell>
                </TableRow>
              )}
              {projection.map((p, i) => {
                const covered = p.paidFromCredit >= daily && daily > 0;
                return (
                  <TableRow key={i} sx={{
                    bgcolor: covered
                      ? (t) => alpha(t.palette.success.main, 0.05)
                      : p.paidFromCredit > 0
                        ? (t) => alpha(t.palette.warning.main, 0.06)
                        : 'transparent',
                    '&:hover': {
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                    },
                  }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>
                      {shortDate(p.date)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...TNUM, fontSize: 12 }}>
                      {fmt(daily)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...TNUM, fontSize: 12,
                      color: p.paidFromCredit > 0 ? 'success.dark' : 'text.disabled',
                      fontWeight: p.paidFromCredit > 0 ? 700 : 500 }}>
                      {p.paidFromCredit > 0 ? fmt(p.paidFromCredit) : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ ...TNUM, fontSize: 12,
                      color: p.addedArrears > 0 ? 'error.main' : 'text.disabled',
                      fontWeight: p.addedArrears > 0 ? 700 : 500 }}>
                      {p.addedArrears > 0 ? fmt(p.addedArrears) : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ ...TNUM, fontSize: 12,
                      color: p.credit > 0 ? 'success.dark' : 'text.disabled',
                      fontWeight: p.credit > 0 ? 700 : 500 }}>
                      {fmt(p.credit)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...TNUM, fontSize: 12,
                      color: p.arrears > 0 ? 'error.main' : 'text.disabled',
                      fontWeight: p.arrears > 0 ? 700 : 500 }}>
                      {fmt(p.arrears)}
                    </TableCell>
                    <TableCell>
                      <OutcomeChip
                        paidFromCredit={p.paidFromCredit}
                        daily={daily}
                        addedArrears={p.addedArrears} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="caption" color="text.secondary"
          sx={{ display: 'block', mt: 1.5, fontStyle: 'italic' }}>
          Projection assumes today's payment is approved and no further payments happen.
          It follows the school's collection week; real school days follow the admin's
          session decisions.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.default' }}>
        <Divider sx={{ flex: 1 }} />
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
}
